import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { LOCATIONS } from "../constants";
import { getUsers, awardGems, adminUpdateUser, deleteUser, createUser } from "../services/api";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { UserFilters } from "../components/users/UserFilters";
import { UsersTable } from "../components/users/UsersTable";
import { AwardGemsDialog, AddUserDialog, EditUserDialog, DeleteUserDialog } from "../components/users/UserDialogs";

const emptyNew = { firstName: "", lastName: "", email: "", password: "", role: "Employee", primaryLocation: LOCATIONS[0], birthdate: "", startDate: "" };

export default function UsersPage() {
  const { user, refresh } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [loc, setLoc] = useState("all");
  const canManage = user.role === "HR" || user.role === "Director";
  const canFilterLoc = canManage;

  // Award dialog
  const [target, setTarget] = useState(null);
  const [amt, setAmt] = useState("");
  const [reason, setReason] = useState("");
  const [awardOpen, setAwardOpen] = useState(false);

  // Edit dialog
  const [editTarget, setEditTarget] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [editLoc, setEditLoc] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);

  // Delete dialog
  const [delTarget, setDelTarget] = useState(null);

  // Add user dialog
  const [addOpen, setAddOpen] = useState(false);
  const [newU, setNewU] = useState(emptyNew);
  const [addErr, setAddErr] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const setNewField = (k, v) => setNewU((p) => ({ ...p, [k]: v }));

  const load = () => getUsers().then(setList).catch(() => {});
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => list.filter((u) => {
    if (q && !`${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (role !== "all" && u.role !== role) return false;
    if (canFilterLoc && loc !== "all" && u.primaryLocation !== loc) return false;
    return true;
  }), [list, q, role, loc, canFilterLoc]);

  const openAdd = () => { setNewU(emptyNew); setAddErr(""); setAddOpen(true); };
  const doAdd = async () => {
    setAddErr("");
    if (!newU.firstName.trim() || !newU.lastName.trim()) { setAddErr("First and last name are required."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newU.email)) { setAddErr("Enter a valid email."); return; }
    if (newU.password.length < 6) { setAddErr("Temporary password must be at least 6 characters."); return; }
    setAddBusy(true);
    try {
      await createUser({
        firstName: newU.firstName, lastName: newU.lastName, email: newU.email, password: newU.password,
        role: newU.role, primaryLocation: newU.primaryLocation, birthdate: newU.birthdate, startDate: newU.startDate,
      });
      setAddOpen(false);
      await load();
      toast.success(`${newU.firstName} ${newU.lastName} was added.`);
    } catch (e) {
      setAddErr(e?.response?.data?.detail || "Could not create user.");
    } finally { setAddBusy(false); }
  };

  const openAward = (u) => { setTarget(u); setAmt(""); setReason(""); setAwardOpen(true); };
  const doAward = async () => {
    if (!amt || Number(amt) === 0) { toast.error("Enter a gems amount."); return; }
    try {
      await awardGems(target.id, Number(amt), reason || "Gems awarded");
      setAwardOpen(false);
      await load();
      await refresh();
      toast.success(`Awarded ${amt} gems to ${target.firstName}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not award gems.");
    }
  };

  const openEdit = (u) => {
    setEditTarget(u); setEditRole(u.role); setEditLoc(u.primaryLocation);
    setEditActive(u.isActive !== false); setEditOpen(true);
  };
  const doEdit = async () => {
    setEditBusy(true);
    try {
      await adminUpdateUser(editTarget.id, { role: editRole, primaryLocation: editLoc, isActive: editActive });
      setEditOpen(false);
      await load();
      if (editTarget.id === user.id) await refresh();
      toast.success(`Updated ${editTarget.firstName}'s account.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update user.");
    } finally { setEditBusy(false); }
  };

  const doApprove = async (u) => {
    try {
      await adminUpdateUser(u.id, { isActive: true });
      await load();
      toast.success(`${u.firstName} ${u.lastName} was approved and can now sign in.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve user.");
    }
  };

  const doDelete = async () => {
    if (!delTarget) return;
    try {
      await deleteUser(delTarget.id);
      await load();
      toast.success(`${delTarget.firstName} ${delTarget.lastName} was deleted.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete user.");
    } finally { setDelTarget(null); }
  };

  const title = user.role === "Manager" ? `${user.primaryLocation} Team` : "All Users";

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-vault-panel/50">Directory</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-vault-panel mt-1">{title}</h1>
        </div>
        {canManage && (
          <button onClick={openAdd} className="inline-flex items-center gap-2 rounded-2xl bg-vault-panel text-vault-cream font-semibold px-5 py-3 hover:bg-vault-panel2 transition-colors" data-testid="add-user-button">
            <UserPlus className="h-4 w-4" /> Add User
          </button>
        )}
      </div>

      <UserFilters q={q} setQ={setQ} role={role} setRole={setRole} loc={loc} setLoc={setLoc} canFilterLoc={canFilterLoc} />

      <UsersTable
        rows={rows} currentUserId={user.id} canManage={canManage}
        onAward={openAward} onEdit={openEdit} onDelete={setDelTarget} onApprove={doApprove}
      />

      <AwardGemsDialog open={awardOpen} onOpenChange={setAwardOpen} target={target} amt={amt} setAmt={setAmt} reason={reason} setReason={setReason} onSubmit={doAward} />
      <AddUserDialog open={addOpen} onOpenChange={setAddOpen} newU={newU} setNewField={setNewField} addErr={addErr} addBusy={addBusy} onSubmit={doAdd} />
      <EditUserDialog open={editOpen} onOpenChange={setEditOpen} editTarget={editTarget} editRole={editRole} setEditRole={setEditRole} editLoc={editLoc} setEditLoc={setEditLoc} editActive={editActive} setEditActive={setEditActive} editBusy={editBusy} currentUserId={user.id} onSubmit={doEdit} />
      <DeleteUserDialog delTarget={delTarget} onOpenChange={(o) => { if (!o) setDelTarget(null); }} onConfirm={doDelete} />
    </div>
  );
}
